import { Component, Inject, Input, model, Optional } from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { NgxSpinnerService, NgxSpinnerComponent } from 'ngx-spinner';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { DeleteIncentiveProgramDialogComponent } from '../contributor-incentive-program/dialogs/delete-incentive-program-dialog/delete-incentive-program-dialog.component';
import { ManageCommissionRatePlanDialogComponent } from './dialogs/manage-commission-rate-plan-dialog/manage-commission-rate-plan-dialog.component';
import { ContributorCommissionRatePlanService } from '../../../../services/contributor-commission-rate-plan.service';
import { ViewCommissionRatePlanPendingApprovalsDialogComponent } from './dialogs/view-pending-approvals/view-pending-approvals.component';
import { DialogDeleteComponent } from '../../../shared/dialog-delete/dialog-delete.component';
import { CommonService } from '../../../../services/common.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter, take } from 'rxjs';

@Component({
  selector: 'app-contributor-commission-rate',
  standalone: true,
  imports: [
    MatTabsModule,
    CommonModule,
    MatTableModule,
    MatMenuModule,
    MatIcon,
    MatButtonModule,
    MatTooltipModule,
    NgxSpinnerComponent
  ],
  templateUrl: './contributor-commission-rate.component.html',
  styleUrl: './contributor-commission-rate.component.scss',
})
export class ContributorCommissionRatePlanComponent {
  @Input() user: any;
  hideDiv: boolean = false;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() private dialogRef: MatDialogRef<ContributorCommissionRatePlanComponent>,
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private commonService: CommonService,
    private commissionRatePlanService: ContributorCommissionRatePlanService
  ) {
    if (data && data.user) {
      this.user = data.user;
      this.hideDiv = true;
    }
  }

  userRoleRights: string[] = [];

  selectedTabIndex = 0;
  dataSource = new MatTableDataSource<any>([]);

  pendingApprovalDataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [
    'name',
    'contributionPeriodName',
    'effectiveDate',
    'isPendingApprovalExists',
    'actions',
  ];
  pendingApprovalsColumns: string[] = ['submittedby', 'submittedon', 'view'];

  ngOnInit() {
    
    //Get roles for access control
    this.commonService.roles$.pipe(filter((roles): roles is string[] => Array.isArray(roles) && roles.length > 0),take(1)).subscribe((roles) => {
        this.userRoleRights = roles;

        //Remove actions column if user doesn't have approve/delete rights
      if (!this.userRoleRights?.includes('ApproveCommissionPlansConfigurationContributors')) {
        this.displayedColumns = this.displayedColumns.filter(column => column !== 'actions');
        this.pendingApprovalsColumns = this.pendingApprovalsColumns.filter(column => column !== 'view');
        }

      });

    this.loadCommissionRatePlans();
    this.loadPendingApprovals();
  }
  loadCommissionRatePlans() {
    this.commissionRatePlanService.getCommissionRatePlanByContributorId(this.user.id).subscribe({
        next: (data: any[]) => {
          this.dataSource.data = data;
        },
        error: (error: any) => {
          console.error('Error loading commission rate plans:', error);
          this.dataSource.data = []; // Reset on error
        },
      });
  }

  loadPendingApprovals() {
    this.commissionRatePlanService
      .getContributorCommissionPendingApprovals(this.user.id)
      .subscribe({
        next: (data) => {
          if (data && Array.isArray(data)) {
            this.pendingApprovalDataSource.data = data;
          } else {
            this.pendingApprovalDataSource.data = []; // Reset if data is not in expected format
          }
        },
        error: (error) => {
          console.error('Error loading pending approvals:', error);
          this.pendingApprovalDataSource.data = []; // Reset on error
        },
      });
  }
  openDetailsMenu(detailsMenuTrigger: MatMenuTrigger) {
    detailsMenuTrigger.openMenu();
  }

  closeMenu(detailsMenuTrigger: MatMenuTrigger) {
    detailsMenuTrigger.closeMenu();
  }

  openAddIncentiveProgramDialog() {
    const existingEffectiveDates = this.dataSource.data.map((plan: any) => plan.effectiveDate);
    const dialogRef = this.dialog.open(ManageCommissionRatePlanDialogComponent, {
        ...this.commonService.dialog_large,
        data: {
          user: this.user,
          mode: 'add',
          existingEffectiveDates,
        },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.loadCommissionRatePlans();
      this.loadPendingApprovals();
    });
  }

  hasPendingApproval(row: any): boolean {
    return row?.isPendingApprovalExists === true;
  }

  openEditIncentiveProgramDialog(existingData: any) {
    if (this.hasPendingApproval(existingData)) return;
    const existingEffectiveDates = this.dataSource.data
      .filter((plan: any) => plan.id !== existingData.id)
      .map((plan: any) => plan.effectiveDate);
    const dialogRef = this.dialog.open(ManageCommissionRatePlanDialogComponent, {
        ...this.commonService.dialog_large,
        data: {
          user: this.user,
          mode: 'edit',
          data: existingData,
          existingEffectiveDates,
        },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.loadCommissionRatePlans();
      this.loadPendingApprovals();
    });
  }

  openViewIncentiveProgramDialog(existingData: any) {
    const dialogRef = this.dialog.open(
      ManageCommissionRatePlanDialogComponent,
      {
        ...this.commonService.dialog_large,
        data: {
          user: this.user,
          mode: 'view',
          data: existingData,
        },
      },
    );

    dialogRef.afterClosed().subscribe(() => {
      this.loadCommissionRatePlans();
      this.loadPendingApprovals();
    });
  }

  openDeleteIncentiveProgramDialog(existingData: any) {
    this.dialog.open(DeleteIncentiveProgramDialogComponent);
  }

  openViewPendingApprovalDetailsDialog(data: any) {
    const dialogRef = this.dialog.open(
      ViewCommissionRatePlanPendingApprovalsDialogComponent,
      {
        ...this.commonService.dialog_large,
        data: {
          user: this.user,
          data: data,
        },
      }
    );
  }

  openDeleteDialog(id: any): void {
    if (this.hasPendingApproval(id)) return;
    const dialogRef = this.dialog.open(DialogDeleteComponent, {
      ...this.commonService.dialog_small,
      data: { action: 'commissionRatePlan', id }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.loadCommissionRatePlans();
        this.loadPendingApprovals();
      }
    });
  }

  isPastEffectiveDate(effectiveDate: string | Date): boolean {
    if (!effectiveDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const effDate = new Date(effectiveDate);
    effDate.setHours(0, 0, 0, 0);

    return effDate < today;
  }
}
